/**
 * Jertscript - A French-themed, sarcastic programming language
 *
 * "Honestly, TypeScript could use more croissants." 🥐
 */

import { Lexer, LexerError } from './lexer';
import { Parser, ParserError } from './parser';
import { Generator, generate } from './generator';

export const VERSION = '0.1.0';

// Re-export core modules
export { Lexer } from './lexer';
export type { LexerError } from './lexer';
export { Parser } from './parser';
export type { ParserError } from './parser';
export { Generator, generate } from './generator';

// Re-export token types
export { TokenType, createToken } from './lexer/tokens';
export type { Token } from './lexer/tokens';

// Re-export AST types
export * from './parser/ast';

// Re-export error types
export {
  ErrorCategory,
  LexerErrorType,
  ParserErrorType,
  TypeErrorType,
  RuntimeErrorType,
  WarningType,
  getErrorMessage,
  getParserErrorMessage,
  formatErrorWithLine,
} from './errors/messages';

// Re-export XION stdlib
export {
  connecter,
  deconnecter,
  solde,
  envoyer,
  executer,
  requete,
  obtenirPortefeuille,
  estConnecte,
  initialiserXion,
  xionStdlib,
} from './stdlib/xion';
export type {
  PortefeuilleXion,
  SoldeInfo,
  ResultatTransaction,
  MessageContrat,
  RequeteContrat,
} from './stdlib/xion';

/**
 * Transpile error interface
 */
export interface TranspileError {
  message: string;
  line: number;
}

/**
 * Transpile result interface
 */
export interface TranspileResult {
  js: string;
  errors: TranspileError[];
}

/**
 * Convenience function to transpile Jertscript code to JavaScript
 */
export function transpile(source: string): TranspileResult {
  const lexer = new Lexer(source);
  const tokens = lexer.scanTokens();
  const lexerErrors = lexer.getErrors();

  if (lexerErrors.length > 0) {
    return {
      js: '',
      errors: lexerErrors,
    };
  }

  const parser = new Parser(tokens);
  const ast = parser.parse();
  const parserErrors = parser.getErrors();

  if (parserErrors.length > 0) {
    return {
      js: '',
      errors: parserErrors,
    };
  }

  const generator = new Generator(ast);
  const js = generator.generate();

  return {
    js,
    errors: [],
  };
}

export { VERSION as default };
